import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Sort, SortDirection } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { HttpClient, HttpParams } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { ProbateSummary } from '../Models/ProbateSummary';
import { User } from '../Models/User';
import {
  ProbateListDateField, ProbateListFilter, ProbateListIdsResult, ProbateListPage, ProbateListUserRole
} from '../Models/ProbateListFilter';
import moment, { Moment } from 'moment';
import { UserService } from '../services/user.service';
import { PROBATE_LIST_FILTERS_KEY, ProbateListFilterStorageService } from '../services/probate-list-filter-storage.service';

// What's persisted between visits. Older saved shapes (with the separate search panel's
// "activeView") still load - unknown fields are ignored and missing ones fall back to defaults.
interface StoredProbateListFilters {
  selectedStatusFilters: number[];
  selectedUserFilter: number | 'unassigned' | null;
  userRole?: ProbateListUserRole;
  showNonCharitable: boolean;
  nameSearchValue: string;
  probateSearchValue: string;
  dateField?: ProbateListDateField;
  startDate: string | null;
  endDate: string | null;
  sortColumn?: string;
  sortDirection?: SortDirection;
}

@Component({
  selector: 'app-probate-list',
  templateUrl: './probate-list.component.html',
  styleUrls: ['./probate-list.component.css'],
  standalone: false
})
export class ProbateListComponent implements OnInit, OnDestroy {
  @Input({ required: true }) loggedInUser?: User = undefined;

  private http = inject(HttpClient);
  route = inject(ActivatedRoute);
  router = inject(Router);

  displayedColumns: string[] = [
    'select',
    'caseReferenceNumber',
    'fullNameOfDeceased',
    'clientCharitableBequestCount',
    'nonClientCharitableBequestCount',
    'charitableBequestCount',
    'nonCharitableBequestCount',
    'dateOfIssue',
    'weekOld',
    'assignedUser',
    'hasNotes',
    'status'
  ];

  // Holds just the current page. Paging and sorting happen on the server (see loadPage), so
  // the paginator and sort are deliberately NOT attached to this data source.
  dataSource = new MatTableDataSource<ProbateSummary>([]);
  users: User[] = [];

  // ---- Filter state (one filter - every criterion combines) ----
  selectedUserFilter: User | 'unassigned' | null = null;
  userRole: ProbateListUserRole = ProbateListUserRole.AssignedTo;
  userRoleOptions = [
    { value: ProbateListUserRole.AssignedTo, label: 'Assigned to' },
    { value: ProbateListUserRole.ReportedBy, label: 'Reported by' },
    { value: ProbateListUserRole.VerifiedBy, label: 'Verified by' },
    { value: ProbateListUserRole.Any, label: 'Any of these' }
  ];

  // Status filter options matching EstateWorkflowStatus enum (numeric values)
  statusOptions = [
    { value: 0, label: 'Unreadable' },
    { value: 1, label: 'Processing' },
    { value: 2, label: 'Reported' },
    { value: 3, label: 'Verified' },
    { value: 4, label: 'Completed' },
    { value: 5, label: 'On Hold' }
  ];
  // Default: Processing (1), Reported (2)
  selectedStatusFilters: number[] = [1, 2];

  showNonCharitable = false;

  dateField: ProbateListDateField = ProbateListDateField.DateOfIssue;
  dateFieldOptions = [
    { value: ProbateListDateField.DateOfIssue, label: 'Date of Probate' },
    { value: ProbateListDateField.Reported, label: 'Reported' },
    { value: ProbateListDateField.Verified, label: 'Verified' },
    { value: ProbateListDateField.Completed, label: 'Completed' }
  ];
  startDate: Moment | null = null;
  endDate: Moment | null = null;

  nameSearchValue = '';
  probateSearchValue = '';

  // ---- Paging / sorting (server-side) ----
  pageSize = 50;
  pageIndex = 0;
  totalCount = 0;
  sortColumn = '';
  sortDirection: SortDirection = '';

  // ---- Selection ----
  // Tracked by probate id, not row object, so a selection survives paging, re-sorting and
  // reloads (each of which brings back fresh row objects).
  // Ids are strings (see utils/probate-id.ts) - a number key could round two different
  // high-numbered probates to the same value.
  selectedIds = new Set<string>();
  assignToUserControl = new FormControl<User | 'unassign' | null>(null);

  private loggedInUserTag = "";
  private loggedInUser2?: User = undefined;
  private hasInitialisedUserFilter = false;
  // A saved user-filter id waiting to be matched against `users` once it loads.
  // `undefined` = nothing to restore; `null` / `'unassigned'` are valid restored values.
  private pendingUserFilterId: number | 'unassigned' | null | undefined = undefined;

  constructor(
    private toastr: ToastrService,
    private userService: UserService,
    private filterStorage: ProbateListFilterStorageService
  ) { }

  ngOnInit(): void {
    const savedPageSize = localStorage.getItem('probate-page-size');
    if (savedPageSize) {
      this.pageSize = +savedPageSize;
    }

    // A reset was explicitly requested (logout/login, or the "Probate List" nav button - even if
    // pressed while already on this screen) - skip restoring anything and start from the plain
    // defaults this component was constructed with. Must win even if an outgoing instance of
    // this same component just re-saved its old filters a moment ago - see
    // ProbateListFilterStorageService.requestReset for why that race exists.
    if (this.filterStorage.consumeResetRequest()) {
      localStorage.removeItem(PROBATE_LIST_FILTERS_KEY);
    } else {
      this.restoreFiltersFromStorage();
    }

    this.extractRouteValues();
    this.loadUsersThenList();
  }

  ngOnDestroy(): void {
    // The app's RouteReuseStrategy destroys this component on every navigation away
    // (including browser back), so this is the one reliable place to persist the
    // filters the user had selected for when they return to the list.
    this.saveFiltersToStorage();
  }

  private restoreFiltersFromStorage(): void {
    const saved = localStorage.getItem(PROBATE_LIST_FILTERS_KEY);
    if (!saved) return;

    try {
      const parsed: StoredProbateListFilters = JSON.parse(saved);
      this.selectedStatusFilters = parsed.selectedStatusFilters ?? this.selectedStatusFilters;
      this.showNonCharitable = parsed.showNonCharitable ?? this.showNonCharitable;
      this.userRole = parsed.userRole ?? ProbateListUserRole.AssignedTo;
      this.nameSearchValue = parsed.nameSearchValue ?? '';
      this.probateSearchValue = parsed.probateSearchValue ?? '';
      this.dateField = parsed.dateField ?? ProbateListDateField.DateOfIssue;
      this.startDate = parsed.startDate ? moment(parsed.startDate) : null;
      this.endDate = parsed.endDate ? moment(parsed.endDate) : null;
      this.sortColumn = parsed.sortColumn ?? '';
      this.sortDirection = parsed.sortDirection ?? '';
      this.pendingUserFilterId = parsed.selectedUserFilter ?? null;
      // A restored filter set (even an explicit "All users") takes precedence over the
      // Supervisor auto-select-self default in loadUsersThenList().
      this.hasInitialisedUserFilter = true;
    } catch {
      // Corrupt/old-shape data - ignore and fall back to defaults.
    }
  }

  private saveFiltersToStorage(): void {
    const toStore: StoredProbateListFilters = {
      selectedStatusFilters: this.selectedStatusFilters,
      selectedUserFilter: this.selectedUserFilter === 'unassigned'
        ? 'unassigned'
        : (this.selectedUserFilter?.id ?? null),
      userRole: this.userRole,
      showNonCharitable: this.showNonCharitable,
      nameSearchValue: this.nameSearchValue,
      probateSearchValue: this.probateSearchValue,
      dateField: this.dateField,
      startDate: this.startDate ? this.startDate.toISOString() : null,
      endDate: this.endDate ? this.endDate.toISOString() : null,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection
    };
    localStorage.setItem(PROBATE_LIST_FILTERS_KEY, JSON.stringify(toStore));
  }

  extractRouteValues() {
    // Get user from the UserService (shared across components)
    this.loggedInUser2 = this.userService.loggedInUser;
    this.loggedInUserTag = `${this.loggedInUser2?.lastName}, ${this.loggedInUser2?.firstName}`;
  }

  private loadUsersThenList() {
    this.getUsers().then(users => {
      this.users = users.sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });

      // Resolve a restored user-filter id (from storage) against the freshly loaded users
      if (this.pendingUserFilterId !== undefined) {
        this.selectedUserFilter = this.pendingUserFilterId === 'unassigned'
          ? 'unassigned'
          : (this.users.find(u => u.id === this.pendingUserFilterId) ?? null);
        this.pendingUserFilterId = undefined;
      }

      // Supervisors default to their own probates on first visit. hasInitialisedUserFilter is
      // also forced true by restoreFiltersFromStorage() whenever a previously saved filter set
      // exists, specifically so this default doesn't clobber a restored filter.
      if (!this.hasInitialisedUserFilter) {
        this.hasInitialisedUserFilter = true;
        this.applySupervisorSelfDefault();
      }

      // Reported By / Verified By columns - role-gated visibility only
      if (this.canViewReportedAndVerifiedColumns() && !this.displayedColumns.includes('reportedBy')) {
        this.displayedColumns.splice(this.displayedColumns.indexOf('status'), 0, 'reportedBy');
      }
      if (this.canViewReportedAndVerifiedColumns() && !this.displayedColumns.includes('verifiedBy')) {
        this.displayedColumns.splice(this.displayedColumns.indexOf('status'), 0, 'verifiedBy');
      }

      this.loadPage();
    }).catch(err => {
      this.toastr.error(`Could not load users (${err?.status ?? 'error'})`, '', { timeOut: 5000 });
    });
  }

  private applySupervisorSelfDefault() {
    if (this.canAssignProbates()) {
      const self = this.users.find(u => u.id === this.loggedInUser2?.id);
      if (self) this.selectedUserFilter = self;
    }
  }

  // ---- Loading ----

  private buildFilter(): ProbateListFilter {
    let userId: number | null = null;
    let unassigned = false;
    let userRole = this.userRole;

    if (this.isReporterOnly()) {
      // Reporters only ever see probates currently assigned to them
      userId = this.loggedInUser2?.id ?? null;
      userRole = ProbateListUserRole.AssignedTo;
    } else if (this.selectedUserFilter === 'unassigned') {
      unassigned = true;
    } else if (this.selectedUserFilter) {
      userId = this.selectedUserFilter.id;
    }

    return {
      statuses: this.selectedStatusFilters,
      userId,
      userRole,
      unassigned,
      dateField: this.dateField,
      dateFrom: this.startDate ? this.startDate.format('YYYY-MM-DD') : null,
      dateTo: this.endDate ? this.endDate.format('YYYY-MM-DD') : null,
      deceasedName: this.nameSearchValue.trim() || null,
      probateId: this.probateSearchValue.trim() || null,
      includeNonCharitable: this.showNonCharitable,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      sortColumn: this.sortDirection ? this.sortColumn : null,
      sortDirection: this.sortDirection || null
    };
  }

  loadPage() {
    const params = new HttpParams().set('user', this.loggedInUserTag);
    this.http.post<ProbateListPage>('/charitycentral/getprobatelist', this.buildFilter(), { params })
      .subscribe({
        next: page => {
          this.dataSource.data = page.items;
          this.totalCount = page.totalCount;
          this.pageIndex = page.pageIndex;
        },
        error: err => {
          this.toastr.error(`Could not load probates (${err.status})`, '', { timeOut: 5000 });
        }
      });
  }

  // "Apply" - a new result set, so back to page 1 and drop any selection made against the
  // previous one (it could include probates the new filter doesn't show).
  applyFilter() {
    this.pageIndex = 0;
    this.selectedIds.clear();
    this.loadPage();
  }

  clearFilters() {
    this.selectedStatusFilters = [1, 2];
    this.selectedUserFilter = null;
    this.applySupervisorSelfDefault();
    this.userRole = ProbateListUserRole.AssignedTo;
    this.showNonCharitable = false;
    this.dateField = ProbateListDateField.DateOfIssue;
    this.startDate = null;
    this.endDate = null;
    this.nameSearchValue = '';
    this.probateSearchValue = '';
    this.applyFilter();
  }

  clearDates() {
    this.startDate = null;
    this.endDate = null;
  }

  // The role only means something for a specific user
  get isUserRoleEnabled(): boolean {
    return !!this.selectedUserFilter && this.selectedUserFilter !== 'unassigned';
  }

  get hasActiveProbateIdSearch(): boolean {
    return this.probateSearchValue.trim().length > 0;
  }

  pageChanged(event: PageEvent) {
    if (event.pageSize !== this.pageSize) {
      this.pageSize = event.pageSize;
      localStorage.setItem('probate-page-size', String(event.pageSize));
    }
    this.pageIndex = event.pageIndex;
    this.loadPage();
  }

  // Sorting is server-side across the whole result set, so a new sort goes back to page 1.
  // Selection is kept - it's by id, so it's unaffected by row order.
  sortChanged(sort: Sort) {
    this.sortColumn = sort.active;
    this.sortDirection = sort.direction;
    this.pageIndex = 0;
    this.loadPage();
  }

  // ---- Selection ----

  isRowSelected(row: ProbateSummary): boolean {
    return this.selectedIds.has(row.probateId);
  }

  toggleRow(row: ProbateSummary) {
    if (this.selectedIds.has(row.probateId)) {
      this.selectedIds.delete(row.probateId);
    } else {
      this.selectedIds.add(row.probateId);
    }
  }

  isAllSelected(): boolean {
    const pageRows = this.dataSource.data;
    return pageRows.length > 0 && pageRows.every(row => this.selectedIds.has(row.probateId));
  }

  isSomeSelectedOnPage(): boolean {
    return this.dataSource.data.some(row => this.selectedIds.has(row.probateId)) && !this.isAllSelected();
  }

  // Header checkbox: selects/deselects the current page only. Selections on other pages are
  // left alone - use "Select all N matching" to take the whole result set.
  masterToggle() {
    const pageRows = this.dataSource.data;
    if (this.isAllSelected()) {
      pageRows.forEach(row => this.selectedIds.delete(row.probateId));
    } else {
      pageRows.forEach(row => this.selectedIds.add(row.probateId));
    }
  }

  // Offered once the whole visible page is ticked and there are more results beyond it
  get canSelectAllMatching(): boolean {
    return this.isAllSelected() && this.totalCount > this.selectedIds.size;
  }

  selectAllMatching() {
    const params = new HttpParams().set('user', this.loggedInUserTag);
    this.http.post<ProbateListIdsResult>('/charitycentral/getprobatelistids', this.buildFilter(), { params })
      .subscribe({
        next: result => {
          result.probateIds.forEach(id => this.selectedIds.add(id));
          if (result.truncated) {
            this.toastr.warning(
              `Only the first ${result.probateIds.length.toLocaleString()} matching probates were selected - narrow the filter to select the rest.`,
              '', { timeOut: 8000 });
          }
        },
        error: err => {
          this.toastr.error(`Could not select all (${err.status})`, '', { timeOut: 5000 });
        }
      });
  }

  clearSelection() {
    this.selectedIds.clear();
  }

  // ---- Assignment ----

  async assignSelectedProbates() {
    const assignTo = this.assignToUserControl.value;
    if (!assignTo || this.selectedIds.size === 0) return;

    const probateIds = [...this.selectedIds];
    try {
      if (assignTo === 'unassign') {
        await this.unassignProbates(probateIds);
        this.toastr.success(`Unassigned ${probateIds.length} case(s)`, '', { timeOut: 3000 });
      } else {
        await this.saveProbates(probateIds, assignTo);
        this.toastr.success(`Assigned ${probateIds.length} case(s) to ${assignTo.firstName} ${assignTo.lastName}`, '', { timeOut: 3000 });
      }
      this.selectedIds.clear();
      // Reload rather than patch rows locally - an assignment can move rows in or out of the
      // current filter (e.g. "assigned to me"), which only the server can work out.
      this.loadPage();
    } catch (err: any) {
      this.toastr.error(`Assignment failed (${err?.status ?? 'error'})`, '', { timeOut: 5000 });
    }
  }

  async saveProbates(probateIds: string[], assignTo: User) {
    let parameters = new HttpParams()
      .set('useridtoassignto', assignTo.id)
      .set('user', this.loggedInUserTag);
    if (this.loggedInUser2?.id) {
      parameters = parameters.set('actionUserId', this.loggedInUser2.id);
    }

    return await lastValueFrom(this.http.post("/charitycentral/saveprobateuserassignments", probateIds, { params: parameters }));
  }

  async unassignProbates(probateIds: string[]) {
    const parameters = new HttpParams().set('user', this.loggedInUserTag);
    return await lastValueFrom(this.http.post("/charitycentral/unassignprobates", probateIds, { params: parameters }));
  }

  // ---- Misc ----

  goToReport(element: ProbateSummary) {
    this.router.navigate(['/reporting', element.probateId], {
      state: { user: this.loggedInUser2 }
    });
  }

  async getUsers() {
    const parameters = new HttpParams().set('user', this.loggedInUserTag);
    return await lastValueFrom(this.http.get<User[]>('/charitycentral/getallusers', { params: parameters }));
  }

  canAssignProbates(): boolean {
    if (!this.loggedInUser2 || !this.loggedInUser2.roles) {
      return false;
    }
    // Only Supervisor is a senior probate-processing role - SystemAdministrator is a separate
    // concern (Pricing/Admin menu access) and must not grant probate assignment/oversight.
    return this.loggedInUser2.roles.some(role =>
      role.id === 2 || role.code === 'Supervisor'
    );
  }

  // Gates the Reported By / Verified By columns - a pure visibility concern, deliberately
  // broader than canAssignProbates() (which gates actually assigning/overseeing probates and
  // must stay Supervisor-only). System Administrators need to see who reported/verified a
  // probate without being granted assignment/oversight actions.
  canViewReportedAndVerifiedColumns(): boolean {
    if (!this.loggedInUser2 || !this.loggedInUser2.roles) {
      return false;
    }
    return this.loggedInUser2.roles.some(role =>
      role.id === 1 || role.code === 'SystemAdministrator' || role.id === 2 || role.code === 'Supervisor'
    );
  }

  getStatusLabel(status: number): string {
    // 6 = EstateWorkflowStatus.NotInWorkflow - a Deceased Name search or Case Ref lookup can
    // return probates outside the workflow. Not a filter option, so not in statusOptions.
    if (status === 6) return 'Not in workflow';
    return this.statusOptions.find(s => s.value === status)?.label ?? 'Unknown';
  }

  getWeekOld(dateOfIssue: Date | string | null | undefined): number | string {
    if (!dateOfIssue) return '';
    const issueDate = new Date(dateOfIssue);
    if (isNaN(issueDate.getTime())) return '';
    return Math.floor((Date.now() - issueDate.getTime()) / 604800000);
  }

  compareUsers(u1: User | 'unassigned' | null, u2: User | 'unassigned' | null): boolean {
    if (u1 === u2) return true;
    if (typeof u1 === 'object' && u1 !== null && typeof u2 === 'object' && u2 !== null) {
      return u1.id === u2.id;
    }
    return false;
  }

  isReporterOnly(): boolean {
    if (!this.loggedInUser2?.roles) return false;
    // Reporter only if they have Reporter role but NOT SystemAdmin or Supervisor
    const hasReporter = this.loggedInUser2.roles.some(r => r.id === 4 || r.code === 'Reporter');
    const hasAdminOrSupervisor = this.canAssignProbates();
    return hasReporter && !hasAdminOrSupervisor;
  }
}
